'use client';

import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { setSession } from '@/utils/tools';
import { cva } from 'class-variance-authority';
import Link from 'next/link';
import { useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import type { BannerProps } from '../types';

interface MktClickParams {
  jump_type?: number;
  jump_value?: string;
  business_sku?: string;
  sku?: string;
  shop_sku?: string;
}

export default function Banner({
  banner1,
  banner2,
  banner3,
  onMktClick,
  onMktExp,
  isWeb,
}: BannerProps) {
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const { navigate } = useAppNavigation();

  // Banner2 价格信息（避免重复计算）
  const banner2Product = banner2?.list?.[0];

  // cva: banner2 描述层样式
  const banner2DescCva = cva('absolute left-0 right-0 text-center text-primary-black', {
    variants: {
      variant: {
        wap: 'top-[74px] px-[15px] text-12 leading-18',
        web: 'top-[10.88vw] px-[11.46vw] text-[max(1.15vw, 14px)] leading-[1.46vw]',
      },
    },
    defaultVariants: {
      variant: 'wap',
    },
  });

  // cva: banner2 价格按钮区域样式
  const banner2PriceWrapCva = cva('price-wrap absolute flex justify-center', {
    variants: {
      variant: {
        wap: 'bottom-[7px] left-0 right-0 h-[46px]',
        web: 'left-[14.58vw] bottom-[4.53vw] h-[4.74vw]',
      },
    },
    defaultVariants: {
      variant: 'wap',
    },
  });

  // Banner2 专用价格按钮样式
  const banner2RowCva = cva('inline-flex items-center');
  const banner2PriceCva = cva(
    'price notranslate h-full box-border border-black bg-[#e4bc2c] flex items-baseline gap-1',
    {
      variants: {
        variant: {
          wap: 'px-[11px] leading-[47px] border-[2.5px]',
          web: 'px-[11px] leading-[4.74vw] border-[4px]',
        },
      },
      defaultVariants: { variant: 'wap' },
    },
  );
  const banner2NowCva = cva('now-price notranslate text-[#181818] font-bb', {
    variants: { variant: { wap: 'text-[18px]', web: 'text-[1.88vw]' } },
    defaultVariants: { variant: 'wap' },
  });
  const banner2OldCva = cva('old-price notranslate line-through', {
    variants: {
      variant: {
        wap: 'pl-[1px] text-[12px] text-[#666666]',
        web: 'pl-[3px] text-[1.25vw] text-primary-black',
      },
    },
    defaultVariants: { variant: 'wap' },
  });
  const banner2BuyCtaCva = cva(
    'buy-now bg-black text-[#e4bc2c] font-bb text-center flex items-center justify-center',
    {
      variants: {
        variant: {
          wap: 'w-[112px] text-[15px] leading-[46px]',
          web: 'w-[11.16vw] text-[1.56vw] leading-[4.74vw]',
        },
      },
      defaultVariants: { variant: 'wap' },
    },
  );

  // Banner3 专用价格按钮样式
  const banner3PriceBoxCva = cva(
    'price notranslate h-full box-border border-black bg-[#e4bc2c] flex items-baseline',
    {
      variants: {
        variant: {
          wap: 'border-[2.5px] px-[15px] leading-[40px]',
          web: 'border-[4px] px-[0.31vw] leading-[4.74vw]',
        },
      },
      defaultVariants: { variant: 'wap' },
    },
  );
  const banner3NowCva = cva('now-price notranslate text-[#181818] font-bb', {
    variants: { variant: { wap: 'text-[13px]', web: 'text-[1.88vw]' } },
    defaultVariants: { variant: 'wap' },
  });
  const banner3OldCva = cva('old-price notranslate line-through', {
    variants: {
      variant: {
        wap: '',
        web: 'pl-[3px] text-[1.25vw] text-primary-black',
      },
    },
    defaultVariants: { variant: 'web' },
  });
  const banner3BuyCtaCva = cva(
    'buy-now bg-black text-[#e4bc2c] font-bb text-center flex items-center justify-center',
    {
      variants: {
        variant: {
          wap: 'w-[40px] h-[40px] text-[12px]',
          web: 'w-[11.15vw] h-[4.74vw] text-[1.56vw] leading-[5.1vw]',
        },
      },
      defaultVariants: { variant: 'wap' },
    },
  );
  const banner3BottomDescCva = cva('absolute left-0 right-0 text-center text-[#000000]', {
    variants: {
      variant: {
        wap: 'px-10 bottom-20 text-12 leading-18',
        web: 'px-[13.02vw] bottom-[2.5vw] text-[max(1.15vw,14px)] leading-[1.46vw]',
      },
    },
    defaultVariants: { variant: 'wap' },
  });

  // cva: banner3 覆盖层与按钮
  const banner3LeftWrapCva = cva('banner-wrap banner-wrap-left absolute left-0 top-0 h-full', {
    variants: { variant: { wap: 'w-[50%]', web: 'w-[50%]' } },
    defaultVariants: { variant: 'wap' },
  });
  const banner3RightWrapCva = cva(
    'banner-wrap banner-wrap-right absolute right-0 top-0 h-full flex justify-end',
    {
      variants: { variant: { wap: 'w-[50%]' } },
      defaultVariants: { variant: 'wap' },
    },
  );
  const banner3PriceWrapLeftCva = cva('price-wrap w-fit flex items-center', {
    variants: {
      variant: { wap: 'mt-[260px] ml-[20px] h-[40px]', web: 'mt-[31.46vw] ml-[9.11vw] h-[4.74vw]' },
    },
    defaultVariants: { variant: 'wap' },
  });
  const banner3PriceWrapRightCva = cva('price-wrap w-fit ] flex items-center', {
    variants: {
      variant: { wap: 'mt-[310px] mr-[20px] h-[40px]', web: 'mt-[36.3vw] mr-[6.93vw] h-[4.74vw]' },
    },
    defaultVariants: { variant: 'wap' },
  });
  // Banner3 仅复用上面的统一样式（行/价格块/现价/旧价/按钮）
  const banner2Price = useMemo(() => {
    try {
      return priceInfo(userInfo, banner2Product || {}, currency) || {};
    } catch (err) {
      logger.error(err);
      return {} as any;
    }
  }, [userInfo, banner2Product, currency]);
  const banner2HasDiscount = Boolean(
    banner2Price &&
      banner2Price.sell_price &&
      banner2Price.price &&
      banner2Price.sell_price !== banner2Price.price,
  );
  const banner2NowPriceText = useMemo(
    () =>
      formatPriceNumberWithSymbol(
        String(banner2Price?.sell_price || banner2Price?.price || '0'),
        currency,
      ),
    [banner2Price?.sell_price, banner2Price?.price, currency],
  );
  const banner2OldPriceText = useMemo(
    () =>
      banner2HasDiscount ? formatPriceNumberWithSymbol(String(banner2Price.price), currency) : null,
    [banner2HasDiscount, banner2Price?.price, currency],
  );

  const banner3Price = (index: number) => {
    try {
      return priceInfo(userInfo, banner3?.list?.[index] || {}, currency) || {};
    } catch (err) {
      logger.error(err);
      return {} as any;
    }
  };
  const banner3NowPriceText = (index: number) => {
    return formatPriceNumberWithSymbol(
      String(banner3Price(index)?.sell_price || banner3Price(index)?.price || '0'),
      currency,
    );
  };
  const banner3OldPriceText = (index: number) => {
    return formatPriceNumberWithSymbol(String(banner3Price(index)?.price || '0'), currency);
  };

  // Banner1 曝光检测
  const [banner1Ref, banner1InView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // Banner2 曝光检测
  const [banner2Ref, banner2InView] = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  // Banner3 曝光检测
  const [banner3RefLeft, banner3LeftInView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  const [banner3RefRight, banner3RightInView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  const [banner3RefLeftBtn, banner3LeftBtnInView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  const [banner3RefRightBtn, banner3RightBtnInView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  const [banner3RefBottom, banner3BottomInView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // Banner2 按钮（价格区域）曝光检测
  const [banner2BtnRef, banner2BtnInView] = useInView({ threshold: 0.5, triggerOnce: true });

  // 处理Banner1曝光
  useEffect(() => {
    if (banner1InView && banner1.image) {
      onMktExp({
        mkt_name: 'Banner_0',
        shop_sku: Number(banner1.jump_type) === 2 ? `${banner1.jump_value}` : '',
        business_sku: Number(banner1.jump_type) === 2 ? `${banner1.business_sku}` : '',
        product_sku: Number(banner1.jump_type) === 2 ? `${banner1.sku}` : '',
        group_id: Number(banner1.jump_type) === 1 ? `${banner1.jump_value}` : '',
      });
    }
  }, [banner1InView, banner1, onMktExp]);

  // 处理Banner2曝光
  useEffect(() => {
    if (banner2InView && banner2.image) {
      onMktExp({
        mkt_name: 'Banner_1',
        shop_sku: banner2.list ? `${banner2.list[0].shop_sku}` : '',
        business_sku: banner2.list ? `${banner2.list[0].business_sku}` : '',
        product_sku: banner2.list ? `${banner2.list[0].sku}` : '',
        group_id: ``,
      });
    }
  }, [banner2InView, banner2, onMktExp]);
  // Banner2 按钮（价格区域）曝光检测
  useEffect(() => {
    if (banner2BtnInView && banner2.image) {
      onMktExp({
        mkt_name: 'Banner_1_Btn',
        shop_sku: banner2.list ? `${banner2.list[0].shop_sku}` : '',
        business_sku: banner2.list ? `${banner2.list[0].business_sku}` : '',
        product_sku: banner2.list ? `${banner2.list[0].sku}` : '',
        group_id: ``,
      });
    }
  }, [banner2BtnInView, banner2, onMktExp]);

  // 处理Banner3Left曝光
  useEffect(() => {
    if (banner3LeftInView && banner3.image) {
      onMktExp({
        mkt_name: 'Banner_2_0',
        shop_sku: banner3.list ? `${banner3.list[0].shop_sku}` : '',
        business_sku: banner3.list ? `${banner3.list[0].business_sku}` : '',
        product_sku: banner3.list ? `${banner3.list[0].sku}` : '',
        group_id: ``,
      });
    }
  }, [banner3LeftInView, banner3, onMktExp]);

  // 处理Banner3Right曝光
  useEffect(() => {
    if (banner3RightInView && banner3.image) {
      onMktExp({
        mkt_name: 'Banner_2_1',
        shop_sku: banner3.list ? `${banner3.list[1].shop_sku}` : '',
        business_sku: banner3.list ? `${banner3.list[1].business_sku}` : '',
        product_sku: banner3.list ? `${banner3.list[1].sku}` : '',
        group_id: ``,
      });
    }
  }, [banner3RightInView, banner3, onMktExp]);

  useEffect(() => {
    if (banner3LeftBtnInView && banner3.image) {
      onMktExp({
        mkt_name: 'Banner_2_0_Btn',
        shop_sku: banner3.list ? `${banner3.list[0].shop_sku}` : '',
        business_sku: banner3.list ? `${banner3.list[0].business_sku}` : '',
        product_sku: banner3.list ? `${banner3.list[0].sku}` : '',
        group_id: ``,
      });
    }
  }, [banner3LeftBtnInView, banner3, onMktExp]);

  useEffect(() => {
    if (banner3RightBtnInView && banner3.image) {
      onMktExp({
        mkt_name: 'Banner_2_1_Btn',
        shop_sku: banner3.list ? `${banner3.list[1].shop_sku}` : '',
        business_sku: banner3.list ? `${banner3.list[1].business_sku}` : '',
        product_sku: banner3.list ? `${banner3.list[1].sku}` : '',
        group_id: ``,
      });
    }
  }, [banner3RightBtnInView, banner3, onMktExp]);

  useEffect(() => {
    if (banner3BottomInView && banner3.image) {
      onMktExp({
        mkt_name: 'Banner_2_2',
        shop_sku: ``,
        business_sku: ``,
        product_sku: ``,
        group_id: ``,
      });
    }
  }, [banner3BottomInView, banner3, onMktExp]);

  // 处理Banner1点击
  const handleBanner1Click = () => {
    onBannerMktClick('Banner_0', banner1);
    // 根据 jump_type 决定跳转方式
    if (banner1.jump_type === 2) {
      // 跳转到商品详情页
      navigate(`/goods-detail/${banner1.jump_value}`);
    } else {
      // 跳转到商品列表页
      setSession('zl_goodsListEnterPageScene', '活动专题页');
      navigate(`/goods-list/${banner1.jump_value}`);
    }
  };

  // 处理Banner2点击
  const handleBanner2Click = () => {
    onBannerMktClick('Banner_1', banner2.list?.[0]);
    navigate(`/goods-detail/${banner2.shop_sku}`);
  };

  const onBannerMktClick = (mkt_name: string, val: MktClickParams) => {
    try {
      let sdEventParams = {};
      if (mkt_name === 'Banner_0') {
        sdEventParams = {
          shop_sku: Number(val.jump_type) === 2 ? `${val.jump_value}` : '',
          business_sku: Number(val.jump_type) === 2 ? `${val.business_sku}` : '',
          product_sku: Number(val.jump_type) === 2 ? `${val.sku}` : '',
          group_id: Number(val.jump_type) === 1 ? `${val.jump_value}` : '',
        };
      } else {
        sdEventParams = {
          shop_sku: `${val.shop_sku}`,
          business_sku: `${val.business_sku}`,
          product_sku: `${val.sku}`,
          group_id: ``,
        };
      }
      onMktClick(mkt_name, sdEventParams);
    } catch (e) {
      logger.error(e);
    }
  };

  return (
    <div className="banner-section">
      {/* Banner 1 */}
      {banner1.image ? (
        <div
          ref={banner1Ref as any}
          className="banner-1 relative w-full overflow-hidden"
          style={{ aspectRatio: banner1.aspectRatio }}
        >
          <Link
            href={
              banner1.jump_type === 2
                ? `/goods-detail/${banner1.jump_value}`
                : `/goods-list/${banner1.jump_value}`
            }
            className="banner-wrap block w-full h-full relative no-underline"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleBanner1Click();
            }}
          >
            <Image
              src={banner1.image}
              alt="Denim Glasses Banner"
              className="w-full"
              priority
              width={isWeb ? 1920 : 375}
              height={isWeb ? 1080 : 750}
            />
          </Link>
        </div>
      ) : (
        <div
          ref={banner1Ref as any}
          className="banner-1 relative w-full overflow-hidden"
          style={{ aspectRatio: banner1.aspectRatio }}
        />
      )}

      {/* Banner 2 */}
      {banner2.image && (
        <div
          ref={banner2Ref as any}
          className="banner-2 relative w-full overflow-hidden"
          style={{ aspectRatio: banner2.aspectRatio }}
        >
          <Link
            href={`/goods-detail/${banner2.shop_sku}`}
            className="banner-wrap block w-full h-full relative no-underline"
            onClick={(e) => {
              e.preventDefault();
              handleBanner2Click();
            }}
          >
            <Image
              src={banner2.image}
              alt="Denim Glasses Products"
              className="w-full"
              priority
              width={isWeb ? 1920 : 375}
              height={isWeb ? 1080 : 750}
            />

            {/* 描述区域（顶部） */}
            {banner2.description && (
              <div className={banner2DescCva({ variant: isWeb ? 'web' : 'wap' })}>
                <p className="word" dangerouslySetInnerHTML={{ __html: banner2.description }} />
              </div>
            )}

            {/* 价格按钮区域（底部） */}
            {banner2.list && banner2.list.length > 0 && (
              <div
                ref={banner2BtnRef as any}
                className={banner2PriceWrapCva({ variant: isWeb ? 'web' : 'wap' })}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onBannerMktClick('Banner_1_Btn', banner2.list?.[0]);
                  navigate(`/goods-detail/${banner2.shop_sku}`);
                }}
              >
                <div className={banner2RowCva()}>
                  {/* 价格 */}
                  <div className={banner2PriceCva({ variant: isWeb ? 'web' : 'wap' })}>
                    <span className={banner2NowCva({ variant: isWeb ? 'web' : 'wap' })}>
                      {banner2NowPriceText}
                    </span>
                    {banner2OldPriceText && banner2NowPriceText !== banner2OldPriceText && (
                      <span className={banner2OldCva({ variant: isWeb ? 'web' : 'wap' })}>
                        {banner2OldPriceText}
                      </span>
                    )}
                  </div>
                  {/* BUY NOW */}
                  <div className={banner2BuyCtaCva({ variant: isWeb ? 'web' : 'wap' })}>
                    BUY NOW
                  </div>
                </div>
              </div>
            )}
          </Link>
        </div>
      )}

      {/* Banner 3 */}
      {banner3.image && (
        <div
          className="banner-3 relative w-full overflow-hidden cursor-pointer"
          style={{ aspectRatio: banner3.aspectRatio }}
        >
          <Image
            src={banner3.image}
            alt="Denim Glasses Collection"
            className="w-full"
            priority
            width={isWeb ? 1920 : 375}
            height={isWeb ? 1080 : 750}
          />

          {/* 左半区可点击覆盖层 */}
          <Link
            ref={banner3RefLeft as any}
            href={`/goods-detail/${banner3.left_shop_sku}`}
            className={banner3LeftWrapCva()}
            onClick={(e) => {
              e.preventDefault();
              onBannerMktClick('Banner_2_0', (banner3 as any).list?.[0] || {});
              navigate(`/goods-detail/${banner3.left_shop_sku}`);
            }}
          >
            {(banner3 as any).list && (banner3 as any).list.length > 0 && (
              <div
                ref={banner3RefLeftBtn as any}
                className={banner3PriceWrapLeftCva({ variant: isWeb ? 'web' : 'wap' })}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onBannerMktClick('Banner_2_0_Btn', (banner3 as any).list?.[0] || {});
                  navigate(`/goods-detail/${banner3.left_shop_sku}`);
                }}
              >
                <div className={banner3PriceBoxCva({ variant: isWeb ? 'web' : 'wap' })}>
                  <span className={banner3NowCva({ variant: isWeb ? 'web' : 'wap' })}>
                    {banner3NowPriceText(0)}
                  </span>
                  {isWeb &&
                    banner3OldPriceText(0) &&
                    banner3NowPriceText(0) !== banner3OldPriceText(0) && (
                      <span className={banner3OldCva({ variant: isWeb ? 'web' : 'wap' })}>
                        {banner3OldPriceText(0)}
                      </span>
                    )}
                </div>
                <div className={banner3BuyCtaCva({ variant: isWeb ? 'web' : 'wap' })}>
                  {isWeb ? 'BUY NOW' : 'BUY'}
                </div>
              </div>
            )}
          </Link>

          {/* 右半区可点击覆盖层 */}
          <Link
            ref={banner3RefRight as any}
            href={`/goods-detail/${banner3.right_shop_sku}`}
            className={banner3RightWrapCva()}
            onClick={(e) => {
              e.preventDefault();
              onBannerMktClick('Banner_2_1', (banner3 as any).list?.[1] || {});
              navigate(`/goods-detail/${banner3.right_shop_sku}`);
            }}
          >
            {(banner3 as any).list && (banner3 as any).list.length > 1 && (
              <div
                ref={banner3RefRightBtn as any}
                className={banner3PriceWrapRightCva({ variant: isWeb ? 'web' : 'wap' })}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onBannerMktClick('Banner_2_1_Btn', (banner3 as any).list?.[1] || {});
                  navigate(`/goods-detail/${banner3.right_shop_sku}`);
                }}
              >
                <div className={banner3PriceBoxCva({ variant: isWeb ? 'web' : 'wap' })}>
                  <span className={banner3NowCva({ variant: isWeb ? 'web' : 'wap' })}>
                    {banner3NowPriceText(1)}
                  </span>
                  {isWeb &&
                    banner3OldPriceText(1) &&
                    banner3NowPriceText(1) !== banner3OldPriceText(1) && (
                      <span className={banner3OldCva({ variant: isWeb ? 'web' : 'wap' })}>
                        {banner3OldPriceText(1)}
                      </span>
                    )}
                </div>
                <div className={banner3BuyCtaCva({ variant: isWeb ? 'web' : 'wap' })}>
                  {isWeb ? 'BUY NOW' : 'BUY'}
                </div>
              </div>
            )}
          </Link>
        </div>
      )}
      <div className="banner-three-bottom relative" ref={banner3RefBottom as any}>
        {banner3.word_bg && (
          <div>
            <Image
              src={banner3.word_bg}
              alt="Denim Description"
              className="w-full h-auto object-contain"
              priority
              width={isWeb ? 1920 : 375}
              height={isWeb ? 1080 : 750}
            />
          </div>
        )}
        {/* 描述文字 */}
        {banner3.description && (
          <div className={banner3BottomDescCva({ variant: isWeb ? 'web' : 'wap' })}>
            {banner3.description}
          </div>
        )}
      </div>

      {/* no responsive styles; keep markup consistent with Vue */}
    </div>
  );
}
